import { useEffect } from 'react';
import { ref, set, onDisconnect } from 'firebase/database';
import { rtdb } from '../firebase';
import useAuthStore from '../store/authStore';

// Helper function to sanitize email for RTDB key
const sanitizeEmailForKey = (email) => {
  if (!email || typeof email !== 'string') return '';
  return email.toLowerCase().replace(/[.#$[\]]/g, '_');
};

export default function useSetOnlineStatus() {
  const { user } = useAuthStore();

  useEffect(() => {
    if (!user) {
      // Don't log this as it's expected when user is not logged in
      return;
    }

    // Get safe UID (handle both uid and id fields)
    const safeUid = user.uid || user.id || null;
    const userEmail = user.email;

    if (!safeUid && !userEmail) {
      return;
    }

    // Create references for both UID and email-based presence
    const statusRef = safeUid ? ref(rtdb, `status/${safeUid}`) : null;
    const emailStatusRef = userEmail ? ref(rtdb, `statusByEmail/${sanitizeEmailForKey(userEmail)}`) : null;

    // Set online status data
    const onlineData = {
      online: true,
      lastSeen: Date.now(),
      ...(safeUid && { uid: safeUid })
    };

    // Set online: true for UID-based presence
    if (statusRef) {
      set(statusRef, onlineData);
      
      // Set online: false on disconnect for UID-based presence
      onDisconnect(statusRef).set({
        online: false,
        lastSeen: Date.now(),
        ...(safeUid && { uid: safeUid })
      });
    }

    // Set online: true for email-based presence (mirrors UID presence)
    if (emailStatusRef) {
      set(emailStatusRef, onlineData);
      
      // Set online: false on disconnect for email-based presence
      onDisconnect(emailStatusRef).set({
        online: false,
        lastSeen: Date.now(),
        ...(safeUid && { uid: safeUid })
      });
    }

    // Use pagehide/visibilitychange to avoid blocking bfcache
    const markOffline = () => {
      const offlineData = {
        online: false,
        lastSeen: Date.now(),
        ...(safeUid && { uid: safeUid })
      };

      // Try a lightweight beacon where possible
      try {
        const payload = JSON.stringify(offlineData);
        if (navigator.sendBeacon) {
          // Optional: If you expose an endpoint to mirror status, use sendBeacon
          // navigator.sendBeacon('/api/presence', payload);
        }
      } catch {}

      // Update both UID and email-based presence
      if (statusRef) {
        set(statusRef, offlineData);
      }
      if (emailStatusRef) {
        set(emailStatusRef, offlineData);
      }
    };

    const markOnline = () => {
      const onlineData = {
        online: true,
        lastSeen: Date.now(),
        ...(safeUid && { uid: safeUid })
      };

      // Update both UID and email-based presence
      if (statusRef) {
        set(statusRef, onlineData);
      }
      if (emailStatusRef) {
        set(emailStatusRef, onlineData);
      }
    };

    const onPageHide = () => {
      // Fires for bfcache navigations without blocking restore
      markOffline();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        markOffline();
      } else if (document.visibilityState === 'visible') {
        markOnline();
      }
    };

    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      
      // Mark offline on cleanup
      markOffline();
    };
  }, [user]);
} 